import { EMAIL_REGEX, PHONE_REGEX } from "@/util/constants";
import { useServices } from "@/services-context";
import { useTranslation } from "react-i18next";
import React, { useCallback, useRef, useState } from "react";

const sanitizePhone = (value: string) => {
  const processed = value.replace(/[^\d\s\-()+]/g, "");

  if (processed.startsWith("+")) {
    return `+${processed.slice(1).replace(/\+/g, "")}`;
  }

  return processed.replace(/\+/g, "");
};

type FormData = {
  vin: string;
  mpn: string;
  email: string;
  phone: string;
  companyName: string;
};

type FormErrors = {
  vin: string;
  mpn: string;
  email: string;
  phone: string;
  companyName: string;
  file: string;
};

export function useFormFindPartCommon() {
  const apiUrl = "/api/b2c-form";
  const services = useServices();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<FormData>({
    vin: "",
    mpn: "",
    email: "",
    phone: "",
    companyName: "",
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({
    vin: "",
    mpn: "",
    email: "",
    phone: "",
    companyName: "",
    file: "",
  });

  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string>("");

  const changeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      const { name, value } = target;

      let processedValue = value;

      if (name === "phone") {
        processedValue = sanitizePhone(value);
      }

      setFormData((prev) => ({ ...prev, [name]: processedValue }));

      if (formErrors[name as keyof FormErrors]) {
        setFormErrors((prev) => ({ ...prev, [name]: "" }));
      }
      if (serverError) setServerError("");
    },
    [formErrors, serverError],
  );

  const handleFileUpload = async (selectedFile: File): Promise<void> => {
    setFile(selectedFile);
  };

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {
      vin: "",
      mpn: "",
      email: "",
      phone: "",
      companyName: "",
      file: "",
    };

    if (!formData.email.trim()) {
      newErrors.email = t("cart.form.pleaseFillField", "Please fill out this field");
    } else if (!EMAIL_REGEX.test(formData.email.trim())) {
      newErrors.email = t("q0qp94g9qwfquw9e.invalid-email", "Please enter a valid email address");
    }

    const trimmedPhone = formData.phone.trim();
    const hasDigits = /\d/.test(trimmedPhone);

    if (!trimmedPhone) {
      newErrors.phone = t("cart.form.pleaseFillField", "Please fill out this field");
    } else if (!hasDigits) {
      newErrors.phone = t("cart.form.pleaseFillField", "Please fill out this field");
    } else if (!PHONE_REGEX.test(trimmedPhone)) {
      newErrors.phone = t("znvy8ierfberif48.phone-number-short", "Phone number is too short");
    }

    // Validate company name
    if (!formData.companyName.trim()) {
      newErrors.companyName = t("cart.form.pleaseFillField", "Please fill out this field");
    }

    setFormErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    if (isLoading) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setServerError("");

    try {
      const langCode = services.parameters.getVisibleLanguage()?.toUpperCase() || "EN";
      const countryCode = services.parameters.getVisibleCountry()?.toUpperCase() || "LV";

      const formDataToSend = new FormData();
      formDataToSend.append("email", formData.email.trim());
      formDataToSend.append("phone", formData.phone.trim());
      formDataToSend.append("name", formData.companyName.trim());
      formDataToSend.append("form_name", "form_find_part.home_page.stock_parts");
      formDataToSend.append(
        "attributes",
        JSON.stringify({
          vin: formData.vin,
          partName: formData.mpn,
          langCode: langCode,
          countryCode: countryCode,
        }),
      );

      if (file) {
        formDataToSend.append("file", file);
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        body: formDataToSend,
      });

      const text = await response.text();
      let result: {
        errors?: { email?: string[]; name?: string[]; phone?: string[] };
        message?: string;
      } = {};
      if (text) {
        try {
          result = JSON.parse(text);
        } catch {
          result = {};
        }
      }

      const mapServerErrorsToForm = (errors: { email?: string[]; name?: string[]; phone?: string[] } | undefined): Partial<FormErrors> => {
        if (!errors || typeof errors !== "object") return {};
        const first = (arr: string[] | undefined) => (Array.isArray(arr) && arr.length ? arr[0] : "");
        return {
          email: first(errors.email),
          companyName: first(errors.name),
          phone: first(errors.phone),
        };
      };

      if (!response.ok || result.errors) {
        const serverErrors = mapServerErrorsToForm(result.errors);
        const hasFieldErrors = Object.values(serverErrors).some(Boolean);
        if (hasFieldErrors) {
          setFormErrors((prev) => ({ ...prev, ...serverErrors }));
        }
        if (result.message) {
          setServerError(result.message);
        } else if (!response.ok && !hasFieldErrors) {
          setServerError(t("cart.form.serverError", "Something went wrong. Please try again later."));
        }
        setIsLoading(false);
        return;
      }

      setFormData({
        vin: "",
        mpn: "",
        email: "",
        phone: "",
        companyName: "",
      });
      setFile(null);
      setFormErrors({
        vin: "",
        mpn: "",
        email: "",
        phone: "",
        companyName: "",
        file: "",
      });
      setIsSuccess(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      setServerError(t("cart.form.serverError", "Something went wrong. Please try again later."));
    } finally {
      setIsLoading(false);
    }
  }, [formData, file, isLoading, validateForm, services, t]);

  return {
    formData,
    formErrors,
    isLoading,
    changeHandler,
    handleFileUpload,
    handleSubmit,
    isSuccess,
    setIsSuccess,
    setFile,
    serverError,
  };
}
